import React, { useState } from 'react';
import { Row, Col, Card, Input, Button, Alert } from 'antd';
import { toast } from 'react-toastify';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  httpClient,
  useGetCurrentUserQuery,
  useGenerateSupportAccessMutation,
  useRedeemSupportAccessMutation,
} from '@teachur/http-client';
import { isUserSuperAdmin } from '@helpers';

// Enter a user id, click Continue, land on the dashboard logged in as that user.
const AdminSupportAccess = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: currentUser, isLoading: loadingCurrentUser } = useGetCurrentUserQuery();
  const [userId, setUserId] = useState<string>('');
  const [error, setError] = useState<string>();

  const { mutateAsync: generate, isPending: generating } = useGenerateSupportAccessMutation();
  const { mutateAsync: redeem, isPending: redeeming } = useRedeemSupportAccessMutation({
    onSuccess: (data) => {
      localStorage.setItem('accessToken', data.accessToken);
      httpClient.setAuthToken(data.accessToken);
      // Identity changed: drop all cached data from the previous identity so
      // e.g. the old user's notifications/courses aren't reused.
      queryClient.clear();
      navigate({ to: '/' });
    },
  });

  const loading = generating || redeeming;

  if (loadingCurrentUser) {
    return null;
  }

  // Super-admin only, matching the backend's user:impersonate permission.
  if (!currentUser || !isUserSuperAdmin(currentUser)) {
    return (
      <Row gutter={16}>
        <Col span={24}>
          <Alert type="error" message="You are not authorized to view this page." showIcon />
        </Col>
      </Row>
    );
  }

  const handleContinue = async () => {
    setError(undefined);
    const id = Number(userId);
    if (!Number.isInteger(id) || id <= 0) {
      setError('Enter a valid user id.');
      return;
    }

    try {
      const grant = await generate(id);
      await redeem({ code: grant.code });
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to grant support access for this user.');
      toast.error('Failed to grant support access for this user.');
    }
  };

  return (
    <Row gutter={16}>
      <Col span={24}>
        <Card title="Support access" size="small">
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="This logs you in as the target user."
            description="Access is single-use, expires in 15 minutes, and is logged against your admin account. Only use this to debug a specific account."
          />

          {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

          <div style={{ display: 'flex', gap: 8, maxWidth: 420 }}>
            <Input
              placeholder="Target user id"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onPressEnter={handleContinue}
              inputMode="numeric"
              disabled={loading}
            />
            <Button type="primary" loading={loading} onClick={handleContinue}>
              Continue
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default AdminSupportAccess;
